/**
 * Pull a human-readable message out of a caught error.
 *
 * Axios wraps HTTP errors in an `AxiosError` whose `.message` is always a
 * generic "Request failed with status code 409". Backend (NestJS) HttpExceptions
 * serialize as `{ message, statusCode, error }` in the response body — that's
 * the text we actually want to show the user.
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const withResponse = error as {
      response?: { data?: { message?: unknown } };
    };
    const message = withResponse.response?.data?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
    if (Array.isArray(message)) {
      // class-validator returns an array of messages — join into one readable line
      const joined = message
        .filter((m): m is string => typeof m === 'string' && m.trim().length > 0)
        .join('. ');
      if (joined.length > 0) return joined;
    }
    // Got a response but no usable message on it (e.g. an empty array) — this is
    // still a server error response, so fall back rather than reading `.message`
    // below, which for AxiosError is always the generic "Request failed with
    // status code NNN" text described up top.
    return fallback;
  }
  if (error instanceof Error && error.message && error.message.trim().length > 0) {
    return error.message;
  }
  return fallback;
}
